export type IoBuckets = {
  oneByte?: Record<string, number>
  twoByte?: Record<string, number>
  fourByte?: Record<string, number>
  eightByte?: Record<string, string | number>
  variable?: Record<string, string>
}

function bucketsInOrder(io: IoBuckets | null | undefined): Array<Record<string, unknown>> {
  if (!io) return []
  const out: Array<Record<string, unknown>> = []
  for (const k of ['oneByte', 'twoByte', 'fourByte', 'eightByte', 'variable'] as const) {
    const b = io[k]
    if (b && typeof b === 'object') out.push(b as Record<string, unknown>)
  }
  return out
}

export function pickIoFirst(io: IoBuckets | null | undefined, names: string[]): unknown {
  for (const name of names) {
    for (const bucket of bucketsInOrder(io)) {
      if (Object.prototype.hasOwnProperty.call(bucket, name)) return bucket[name]
    }
  }
  return undefined
}

function toNum(v: unknown): number | undefined {
  if (v === undefined || v === null) return undefined
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const n = Number(v)
    return Number.isFinite(n) ? n : undefined
  }
  return undefined
}

export type DerivedTelemetry = {
  ignition: 'on' | 'off' | undefined
  engineTempC: number | undefined
  odometerKm: number | undefined
  fuelPct: number | undefined
  externalVoltageV: number | undefined
  internalVoltageV: number | undefined
  speedKmh: number | undefined
  angleDeg: number | undefined
  address: string | undefined
}

export function deriveTelemetryFromPosition(
  latest: { io?: IoBuckets; speed?: number; angle?: number; address?: string } | null | undefined
): DerivedTelemetry {
  const io = latest?.io as IoBuckets | undefined

  const ignitionRaw = pickIoFirst(io, ['Ignition', 'Digital Input 1'])
  const ignN = toNum(ignitionRaw)
  let ignition: DerivedTelemetry['ignition']
  if (ignN !== undefined) {
    if (ignN === 1 || ignN === 32512) ignition = 'on'
    else if (ignN === 0) ignition = 'off'
  }

  const engineTempC = toNum(
    pickIoFirst(io, ['Engine Temperature', 'Coolant Temperature', 'Engine Oil Temperature'])
  )

  const odomMeters = toNum(pickIoFirst(io, ['Total Odometer']))
  const odometerKm = odomMeters !== undefined ? odomMeters / 1000 : undefined

  let fuelPct = toNum(
    pickIoFirst(io, [
      'Fuel Level',
      'OBD OEM Fuel Level',
      'LLS 1 Fuel Level',
      'LLS 2 Fuel Level',
      'Fuel Level (CAN)',
    ])
  )
  if (fuelPct !== undefined && fuelPct > 100 && fuelPct <= 1000) fuelPct = fuelPct / 10

  const extMv = toNum(pickIoFirst(io, ['External Voltage']))
  const battMv = toNum(pickIoFirst(io, ['Battery Voltage']))

  return {
    ignition,
    engineTempC,
    odometerKm,
    fuelPct,
    externalVoltageV: extMv !== undefined ? extMv / 1000 : undefined,
    internalVoltageV: battMv !== undefined ? battMv / 1000 : undefined,
    speedKmh: latest?.speed,
    angleDeg: latest?.angle,
    address: latest?.address,
  }
}

export function formatDashFr(n: number, unit: string, fractionDigits = 0): string {
  return `${n.toLocaleString('fr-FR', {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} ${unit}`
}
